import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Box,
    Chip,
    FormControl,
    Select,
    MenuItem,
    OutlinedInput,
    CircularProgress
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Button from "../../../../shared/OAWorkflow/components/atoms/Button";
import { getEligibleUsers } from "../../../../shared/OAWorkflow/utils/taskTransitions";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import "../OAWorkflow.css";

/**
 * Dialog for assigning Processors and QA to selected tasks.
 * Matches Figma design.
 */
const AssignDialog = ({ open, onClose, onAssign, isAssigning, selectedTasks, onRemoveTask, users, enableProcessor = true, enableQA = true }) => {
    const [processorId, setProcessorId] = useState("");
    const [qaId, setQaId] = useState("");

    // Reset state when dialog opens
    useEffect(() => {
        if (open) {
            setProcessorId("");
            setQaId("");
        }
    }, [open]);

    const handleAssign = () => {
        onAssign({
            processorId: processorId === "SKIP_PROCESSING" ? "SKIP_PROCESSING" : (processorId ? parseInt(processorId) : null),
            qaId: qaId ? parseInt(qaId) : null
        });
    };

    // COL-137: same candidate set as the per-task assignment dropdowns. The old
    // role-ID filter took the Processor/QA role IDs from whichever users happened
    // to hold those roles, so a list with no primary Processor (or QA) left that
    // dropdown empty.
    // Deactivated users can't take new work, so bulk assignment leaves them out.
    const isAssignable = (u) => !u.oaStatus || u.oaStatus === "active";
    const processors = getEligibleUsers(users, "Processor").filter(isAssignable);
    const qas = getEligibleUsers(users, "QA").filter(isAssignable);

    const outerTheme = useTheme();

    const localTheme = React.useMemo(() => createTheme(outerTheme, {
        typography: {
            fontFamily: 'Lato, sans-serif',
        },
    }), [outerTheme]);

    return (
        <ThemeProvider theme={localTheme}>
            <Dialog
                open={open}
                onClose={isAssigning ? undefined : onClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{ className: "oa-lato-font", sx: { borderRadius: 2, p: 1 } }}
            >
                <DialogTitle sx={{ pb: 1, fontSize: "1.25rem", fontWeight: 600, color: "#1a1a2e" }}>
                    Assign Processor & QA
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: "#4B5563", mb: 2 }}>
                        Processor and QA will be assign to all the selected application numbers
                    </Typography>

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
                        {selectedTasks.map((task) => (
                            <Chip
                                key={task.taskId}
                                label={task.applicationNo || task.taskId}
                                onDelete={isAssigning ? undefined : () => onRemoveTask(task.taskId)}
                                deleteIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                                sx={{
                                    backgroundColor: "#F3F4F6",
                                    color: "#374151",
                                    fontWeight: 500,
                                    borderRadius: 4,
                                    height: 28,
                                    "& .MuiChip-deleteIcon": {
                                        color: "#6B7280",
                                        "&:hover": { color: "#374151" }
                                    }
                                }}
                            />
                        ))}
                        {selectedTasks.length === 0 && (
                            <Typography variant="body2" color="error">No tasks selected.</Typography>
                        )}
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <FormControl fullWidth size="small">
                            <Select
                                displayEmpty
                                value={processorId}
                                onChange={(e) => setProcessorId(e.target.value)}
                                disabled={!enableProcessor || isAssigning}
                                input={<OutlinedInput sx={{ borderRadius: 1.5, color: (processorId && processorId !== 'SKIP_PROCESSING') ? 'inherit' : (processorId === 'SKIP_PROCESSING' ? '#6366F1' : '#9CA3AF') }} />}
                                renderValue={(selected) => {
                                    if (!enableProcessor) return <em style={{ color: '#9CA3AF' }}>Not available for selected tasks</em>;
                                    if (selected === "SKIP_PROCESSING") return <span style={{ color: '#6366F1', fontStyle: 'italic' }}>Processing Skipped</span>;
                                    if (!selected) return <em>Select Processor</em>;
                                    const user = processors.find(p => p.id === selected);
                                    return user ? user.displayName : selected;
                                }}
                            >
                                <MenuItem disabled value="">
                                    <em>Select Processor</em>
                                </MenuItem>
                                {enableProcessor && (
                                    <MenuItem value="SKIP_PROCESSING" sx={{ fontStyle: 'italic', color: '#6366F1' }}>
                                        Assign to QA (Skip Processing)
                                    </MenuItem>
                                )}
                                {processors.map((p) => (
                                    <MenuItem key={p.id} value={p.id}>{p.displayName} ({p.email})</MenuItem>
                                ))}
                            </Select>
                            {processorId === 'SKIP_PROCESSING' && (
                                <Typography variant="caption" sx={{ color: '#EF4444', mt: 0.5, fontStyle: 'italic', ml: 1 }}>
                                    QA selection is required when skipping processing
                                </Typography>
                            )}
                        </FormControl>

                        <FormControl fullWidth size="small">
                            <Select
                                displayEmpty
                                value={qaId}
                                onChange={(e) => setQaId(e.target.value)}
                                disabled={!enableQA || isAssigning}
                                input={<OutlinedInput sx={{ borderRadius: 1.5, color: qaId ? 'inherit' : '#9CA3AF' }} />}
                                renderValue={(selected) => {
                                    if (!enableQA) return <em style={{ color: '#9CA3AF' }}>Not available for selected tasks</em>;
                                    if (!selected) return <em>Select QA</em>;
                                    const user = qas.find(q => q.id === selected);
                                    return user ? user.displayName : selected;
                                }}
                            >
                                <MenuItem disabled value="">
                                    <em>Select QA</em>
                                </MenuItem>
                                {qas.map((q) => (
                                    <MenuItem key={q.id} value={q.id}>{q.displayName} ({q.email})</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ pt: 1, pb: 2, px: 3 }}>
                    <Button
                        variant="secondary"
                        onClick={onClose}
                        disabled={isAssigning}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleAssign}
                        disabled={selectedTasks.length === 0 || isAssigning || (processorId === 'SKIP_PROCESSING' && !qaId)}
                        data-testid="assign-submit-btn"
                        sx={{ minWidth: 100 }}
                    >
                        {isAssigning ? <CircularProgress size={20} color="inherit" /> : "Submit"}
                    </Button>
                </DialogActions>
            </Dialog>
        </ThemeProvider>
    );
};

export default AssignDialog;
